import { ExclusionOverlay, ExclusionOverlayType } from '@api';
import { Button, Typography } from '@mui/material';
import { useParams } from '@tsp-ui/core';
import { FilterTextField, FilterableFilledSection } from '@tsp-ui/core/components';
import { ExclusionOverlayCard } from '@views/admin/investor-eligibility/components/ExclusionOverlayCard';
import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { InvestorEligibilityContext } from '../InvestorEligibilityRoutes';

import styles from './ExclusionOverlaySection.module.scss';


export default function ExclusionOverlaySection({ overlays }: { overlays: ExclusionOverlay[] }) {
    const { loanProgramID: loanProgramId, investorID: investorId } = useParams();

    const variant = loanProgramId ? 'loan-program' : investorId ? 'investor' : 'client';

    const [ searchTerm, setSearchTerm ] = useState('');
    const [ debouncedTerm ] = useDebounce(searchTerm, 300);
    const filteredOverlays = overlays.filter(overlay => overlay.name.toLocaleLowerCase().includes(debouncedTerm));

    const clientOverlays = filteredOverlays.filter(({ type }) => type === ExclusionOverlayType.CLIENT);
    const investorOverlays = filteredOverlays.filter(({ type }) => type === ExclusionOverlayType.INVESTOR);
    const loanProgramOverlays = filteredOverlays.filter(({ type }) => type === ExclusionOverlayType.LOAN_PROGRAM);

    const { investors } = useContext(InvestorEligibilityContext);

    return (
        <FilterableFilledSection
            header="Exclusion overlays"
            addButton={(
                <Button
                    variant="contained"
                    component={Link}
                    to="overlays/add"
                >
                    Add exclusion overlay
                </Button>
            )}
            filterField={(
                <FilterTextField
                    placeholder="Filter exclusion overlays"
                    helperText="Filter by name"
                    onChange={(event) => setSearchTerm(event.target.value.toLocaleLowerCase())}
                />
            )}
            caption={variant === 'client' ? (
                <>
                    Only includes exclusion overlays that apply to more than one investor. View additional{' '}
                    exclusions on an individual Investor or Loan Program.
                </>
            ) : variant === 'investor' ? (
                <>
                    Only includes exclusion overlays that apply to more than one Loan Program for{' '}
                    this investor. View additional excusions on an individual Loan Program.
                </>
            ) : undefined}
        >
            {variant !== 'client' && (
                <Typography
                    variant="caption"
                    color="textSecondary"
                    className={styles.exclusionGroup}
                >
                    Global
                </Typography>
            )}

            {clientOverlays?.length ? clientOverlays?.map(overlay => (
                <ExclusionOverlayCard
                    key={overlay.id}
                    overlay={overlay}
                />
            )) : 'No global overlays are currently configured.'}

            {variant !== 'client' && (
                <>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        className={styles.exclusionGroup}
                    >
                        {investors?.find(investor => investor.id === investorId)?.name}
                    </Typography>

                    {investorOverlays?.length ? investorOverlays?.map(overlay => (
                        <ExclusionOverlayCard
                            key={overlay.id}
                            overlay={overlay}
                        />
                    )) : 'No overlays for this investor are currently configured.'}
                </>
            )}

            {variant === 'loan-program' && (
                <>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        className={styles.exclusionGroup}
                    >
                        This loan program
                    </Typography>

                    {loanProgramOverlays?.length ? loanProgramOverlays?.map(overlay => (
                        <ExclusionOverlayCard
                            key={overlay.id}
                            overlay={overlay}
                        />
                    )) : 'No overlays for this loan program are currently configured.'}
                </>
            )}
        </FilterableFilledSection>
    );
}
